/* What "Urgent" holds (Gabriel, 2026-10-28): the serious alerts, every fuel
   alert, buses off the road (suspended or in maintenance), papers and services
   past due, buses burning well over their own normal, and the fuel set-up gaps.
   The Alerts page's Urgent tab and Overview's count both read this one list,
   so the two never disagree. Incident alerts are not here: each is a driver's
   report, counted and closed in Analytics → Incidents. */
import { kindOf } from '../../fleet/alertMeta';
import type { Bus, FleetAlert, FleetModel } from './model';

const DAY = 86400000;
export const isFuelKind = (k: string) => /fuel|refuel|liters|pump_price|epra/i.test(k);

/** Buses below `ratio` of their own normal km per litre, worst first. 0.9 (about
    10% more fuel) is "above normal" — the Fuel page's red bar and Overview's To
    do; Urgent takes only 0.8, a quarter more fuel or worse. */
export function thirstyBuses(f: FleetModel, ratio = 0.9): Bus[] {
  return f.buses
    .filter((b) => b.status !== 'off_road' && b.kmpl != null && b.kmplNormal != null && b.kmpl < b.kmplNormal * ratio)
    .sort((a, b) => (a.kmpl as number) / (a.kmplNormal as number) - (b.kmpl as number) / (b.kmplNormal as number));
}
export const URGENT_FUEL_RATIO = 0.8;

/** Buses that drove in the last 30 days but logged no refill in the last 7.
    Worked out here, not from Bus.needsLook: that keeps only a bus's first
    problem, so a bus using too much fuel never showed as not logging. */
export function notLoggingBuses(f: FleetModel, now = Date.now()): Bus[] {
  return f.buses.filter((b) => {
    if (b.status === 'off_road' || !(b.km30 && b.km30 > 0)) return false;
    const last = f.refills.find((r) => r.busId === b.id); // refills are newest first
    return !last?.at || now - last.at.getTime() > 7 * DAY;
  });
}

/** "40% more fuel than its normal". */
export const thirstPct = (b: Bus) => Math.round(((b.kmplNormal as number) / (b.kmpl as number) - 1) * 100);

export interface PastDue {
  bus: Bus;
  /** Expired insurance or inspection: the bus is not legal to run. */
  papers: { label: string; due: Date }[];
  /** A service past its date or its kilometres. */
  service: { label: string; kmOver: number | null; due: Date | null } | null;
}

/** Each bus still on the road with papers expired or a service overdue. */
export function pastDue(f: FleetModel, now = Date.now()): PastDue[] {
  const svcState = new Map((f.maintenance || []).map((m) => [m.busId, m.service.state]));
  return f.buses.filter((b) => b.status !== 'off_road').flatMap((b) => {
    const papers = b.papers
      .filter((p) => p.kind !== 'service' && p.due && p.due.getTime() < now)
      .map((p) => ({ label: p.label, due: p.due as Date }));
    const svc = b.papers.find((p) => p.kind === 'service');
    const svcOver = !!svc && ((svc.dueKm != null && svc.dueKm <= 0) || (svc.due != null && svc.due.getTime() < now) || svcState.get(b.id) === 'overdue');
    const service = svc && svcOver ? { label: svc.label, kmOver: svc.dueKm != null && svc.dueKm <= 0 ? -svc.dueKm : null, due: svc.due } : null;
    return papers.length || service ? [{ bus: b, papers, service }] : [];
  });
}

export interface FuelGap { bus: Bus; kind: 'tank' | 'odo' }

/* Fuel set-up gaps, worked out rather than stored (classic HighRiskScreen):
   no tank size makes every litre figure a guess; no odometer reading makes km
   per litre impossible. Each clears itself once fixed — no "mark read". */
export function fuelGaps(f: FleetModel): FuelGap[] {
  const num = (v: unknown) => (typeof v === 'number' && v > 0 ? v : null);
  return f.buses.filter((b) => b.status !== 'off_road').flatMap((b) => {
    const out: FuelGap[] = [];
    if (!num(b.raw.fuelTankCapacityL) && !num(b.raw.fuelCapacityLiters)) out.push({ bus: b, kind: 'tank' });
    if (!num(b.raw.odometerBaselineKm) && !num(b.raw.odometerKm) && !f.refills.some((r) => r.busId === b.id && (r.odometer ?? 0) > 0)) out.push({ bus: b, kind: 'odo' });
    return out;
  });
}

export interface Urgent {
  alerts: FleetAlert[];
  offRoad: Bus[];
  pastDue: PastDue[];
  thirsty: Bus[];
  gaps: FuelGap[];
  count: number;
}

export function urgentOf(f: FleetModel): Urgent {
  const alerts = f.alerts.filter((a) => !a.read && a.kind !== 'incident'
    && (a.severity === 'urgent' || kindOf(a.kind) === 'fuel' || isFuelKind(a.kind)));
  const offRoad = f.buses.filter((b) => b.status === 'off_road');
  const due = pastDue(f);
  const thirsty = thirstyBuses(f, URGENT_FUEL_RATIO);
  const gaps = fuelGaps(f);
  return { alerts, offRoad, pastDue: due, thirsty, gaps, count: alerts.length + offRoad.length + due.length + thirsty.length + gaps.length };
}
